import { Component, Input, OnInit } from '@angular/core';
import { SlotTypeEnum } from '../models/cartDetails/slot-type.enum';
import { CommonInfoService } from '../cart-process/common-info.service';
import { IPriceCycle, IPriceGroup } from '../interfaces/cart-details.interface';
import { MonthForm } from '../utils/pipes/monthForm';
import { PromotionTypeEnum } from '../models/cartDetails/promotionType.model';
import { TrafficTypeEnum } from '../models/cartDetails/trafficType.model';
import { SectionTypeEnum } from '../models/cartDetails/section-type.enum';
import { BundleTypeEnum } from '../models/cartDetails/bundle-type.enum';
import DateFormatPipe from '../utils/pipes/date-format.pipe';
import SlotModel from '../models/slot.model';

interface PriceObject {groups: IPriceGroup; sortedList: PriceList[]}
interface PriceList {key: string; value: IPriceCycle[]}

@Component({
  selector: 'price-group, [price-group]',
  templateUrl: './price-group.component.html',
})
export default class PriceGroupComponent implements OnInit {

  @Input() slot: SlotModel;
  @Input() slotType: SlotTypeEnum;
  @Input() sectionType: SectionTypeEnum;
  @Input() showMonthly: boolean;
  @Input() showOldPrice: boolean;
  @Input() isRetentionBeforeEOP: boolean;
  @Input() isOnlyOneSlot: boolean;
  @Input() isSideOrderSummary = false;

  public offerChangePrices: PriceObject;
  public newContractPrices: PriceObject;
  public pricesList: {pricesType: 'old' | 'new', prices: PriceObject}[] = [];
  public isTooltipVisible = false;
  public translateTooltip = false;
  public isVirginBrand = false;
  public tooltipContent: string;
  public isMixOffer: boolean;
  public isDataOffer: boolean;
  public isLeasing: boolean;
  private monthFormat: DateFormatPipe = new MonthForm();
  private dateFormat: DateFormatPipe = new DateFormatPipe();
  private termOfServiceNumber: number;

  protected readonly SlotTypeEnum = SlotTypeEnum;
  protected readonly SectionTypeEnum = SectionTypeEnum;
  protected readonly BundleTypeEnum = BundleTypeEnum;

  constructor(
    public commonInfoService: CommonInfoService
  ) {
  }

  ngOnInit(): void {
    this.isVirginBrand = this.commonInfoService.isVirginBrand;
    this.isMixOffer = this.commonInfoService.isServiceTypeMix;
    this.isDataOffer = this.slot.trafficType === TrafficTypeEnum.DATA;
    this.isLeasing = this.slot.isLeasing();
    this.offerChangePrices = this.preparePriceList(this.slot.offerChangePrices);
    this.newContractPrices = this.preparePriceList(this.slot.newContractPrices);

    if (this.offerChangePrices?.sortedList?.length) this.pricesList.push({pricesType: 'old', prices: this.offerChangePrices});
    if (this.newContractPrices?.sortedList?.length) this.pricesList.push({pricesType: 'new', prices: this.newContractPrices});


    this.tooltipContent = this.slot.getTooltipContent();
    this.termOfServiceNumber = this.slot.termOfServiceNumber === -1 ? 24 : this.slot.termOfServiceNumber;
  }

  public getContractTimeText(): string {
    if (this.isBeforeEopWithDevice || this.isBeforeEopWithDeviceAndWithout) {
      return `za abonament ${this.offerChangePrices?.groups.subsidy?.length ? 'i urządzenie z obecnej umowy' : ''}`;
    } else if (this.isBeforeEopWithoutDeviceAndWith || this.isBeforeEopWithoutDevice) {
      return `do zakończenia obecnej umowy <br>${this.contractEndingDate} oraz w trakcie nowej umowy`;
    } else {
      return 'za abonament';
    }
  }

  public getContractDescription(pricesType: 'old' | 'new'): string {
    if (pricesType === 'old') {
      if (this.isBeforeEopWithDevice || this.isBeforeEopWithDeviceAndWithout) {
        return `za abonament ${this.slot?.offerChangePrices?.subsidy?.length ? 'i urządzenie' : ''}<br>z obecnej umowy`;
      }

    } else if (pricesType === 'new' && !this.slot.oldContractEndingDate) {
      if (this.isAfterEopWithDevice) {
        return `za abonament i&nbsp;urządzenie płatne przez <b>${this.contractPeriod}</b> nowej umowy`;
      }

      if (this.isAfterEopWithoutDevice) {
        return 'za abonament z nowej umowy';
      }
    } else {
      if (this.isBeforeEopWithDevice || this.isBeforeEopWithDeviceAndWithout) {
        return `za abonament na nowych warunkach, <br>urządzenie z obecnej umowy i&nbsp;usługi dodatkowe, w&nbsp;tym:`;
      }
    }
  }

  private preparePriceList(contractPrices: IPriceGroup) {
    if (!contractPrices || !Object.keys(contractPrices).length) return undefined;

    const groups = Object.keys(contractPrices).reduce((result, item, index, array) => {
      if (contractPrices[item]?.length) {
        if (this.isSummarySectionType && item === 'summaryPriceWithAddons') {
          result['summaryPrice'] = contractPrices[item];
        } else if (!this.isSummarySectionType && item === 'summaryPrice') {
          result['summaryPrice'] = contractPrices[item];
        } else if (item !== 'summaryPrice' && item !== 'summaryPriceWithAddons') {
          result[item] = contractPrices[item];
        }
      }

      return result;
    }, <IPriceGroup>{});

    const list = Object.keys(groups)
      .filter(key => {
        return (key !== 'summaryPrice'
                && !(key === 'selectedAddonsPrice'
                && (this.slotType === SlotTypeEnum.MEMBER || this.sectionType === SectionTypeEnum.SIDEBAR)));
              })
      .map(key => {
        return {
          key,
          value: contractPrices[key]
        };
      });

    return {
      groups,
      sortedList: list
    };
  }

  private get contractPeriod(): string | null {
    return this.slot.termOfServiceNumber > 0 ? `${this.slot.termOfServiceNumber}&nbsp;${this.monthFormat.transform(this.slot.termOfServiceNumber)}` : this.slot.termOfServiceNumber === -1 ? 'cały okres trwania umowy' : null;
  }

  private get contractEndingDate(): string {
    return `(${this.dateFormat.transform(this.slot.oldContractEndingDate)})`;
  }

  private get isSummarySectionType(): boolean {
    return this.sectionType === SectionTypeEnum.SUMMARY;
  }

  /*
   * Dla offerChangePrices nie występują installments a subsidy.
   * Subsidy równe 0 nie przychodzą w CD.
   */

  // 1. przed EOP - miał telefon - przedłuża z telefonem
  private get isBeforeEopWithDevice(): boolean {
    return !!this.slot.oldContractEndingDate && this.newContractPrices && !!this.offerChangePrices?.groups.installments;
  }

  // 2. przed EOP - miał telefon - przedłuża bez telefonu
  private get isBeforeEopWithDeviceAndWithout(): boolean {
    return !!this.slot.oldContractEndingDate && this.newContractPrices && !this.offerChangePrices?.groups.installments;
  }

  // 3. przed EOP - nie miał telefonu - przedłuża z telefonem
  private get isBeforeEopWithoutDeviceAndWith(): boolean {
    return !!this.slot.oldContractEndingDate && !this.newContractPrices && !!this.offerChangePrices?.groups.installments;
  }

  // 4. przed EOP - nie miał telefonu - przedłuża bez telefonu
  private get isBeforeEopWithoutDevice(): boolean {
    return !!this.slot.oldContractEndingDate && !this.newContractPrices && !this.offerChangePrices?.groups.installments;
  }

  // 6. po EOP - z telefonem w subsydiach
  private get isAfterEopWithDevice(): boolean {
    return (this.slot.promotionType === PromotionTypeEnum.LEASING || this.slot.promotionType === PromotionTypeEnum.REGULAR) && this.slot.hasDevice;
  }

  // 7. po EOP - bez telefonu
  private get isAfterEopWithoutDevice(): boolean {
    return (this.slot.promotionType === PromotionTypeEnum.LEASING || this.slot.promotionType === PromotionTypeEnum.REGULAR) && !this.slot.hasDevice;
  }

  public getRetentionText({cycleStart, cycleEnd, endDate, startDate}: IPriceCycle, index: number, isSteps?: boolean): string {
    if (this.slot.termOfServiceNumber === -1) {
      return `przez <b>${this.termOfServiceNumber} ${this.monthFormat.transform(this.termOfServiceNumber)}</b>`;
    } else if (endDate) {
      return `do <b>${this.dateFormat.transform(endDate)}</b>`;
    } else if (startDate && isSteps) {
      return `później <b> od ${this.dateFormat.transform(startDate)}</b> <br> <b>przez ${cycleEnd} ${this.monthFormat.transform(cycleEnd)} </b>`;
    } else if (startDate) {
      return index
        ? `później <b>przez ${cycleEnd} ${this.monthFormat.transform(cycleEnd)}</b>`
        : `później od <b>${this.dateFormat.transform(startDate)}</b>`;
    } else {
      if (!cycleStart) return '';

      if (cycleStart === 1 && (!isSteps || !this.isRetentionBeforeEOP)) {
        if (cycleEnd === 0) {
          return '';
        } else if (cycleEnd === 1) {
          return this.isRetentionBeforeEOP ? 'pierwszy miesiąc' : '<b>pierwszy miesiąc</b>';
        } else {
          return !index
          ? `przez <b>${cycleEnd} ${this.monthFormat.transform(cycleEnd)}</b>`
          : `później <b>przez ${cycleEnd} ${this.monthFormat.transform(cycleEnd)}</b>`;
        }
      } else {
        return isSteps
          ? `${!this.isRetentionBeforeEOP ? '' : 'później'} <b> od ${cycleStart} do ${cycleEnd} miesiąca</b>`
          : `później <b>przez ${cycleEnd} ${this.monthFormat.transform(cycleEnd)}</b>`;
      }
    }
  }
}
